import type { ServerProvider } from "@cmmd-forge/contracts";
import { isServerProviderExecutable } from "@cmmd-forge/shared/providerExecution";

/**
 * Why the model picker has nothing to show.
 *
 * ── the failure this exists to make impossible ──────────────────────────────
 *
 * The picker withholds models for SEVEN independent reasons — org provider
 * policy, `isServerProviderExecutable`, the live provider snapshot, the
 * published catalog, last-known catalog reconstitution, target-environment
 * resolution, and the static picker provider list. Until this module they
 * rendered through two booleans into three strings, so most causes displayed a
 * sentence written for a different cause.
 *
 * On 2026-08-07 that cost three debugging rounds: no project was selected, and
 * the picker said "Models load when this project starts. Send to start it."
 * Both conditions reach the picker as `providers === undefined`, so one boolean
 * could not tell them apart. The message named the wrong cause AND prescribed
 * the wrong action — sending would not have helped, because there was nothing
 * to send to. A stale catalog was investigated at length; it was fine.
 *
 * The 30-day fix chain on this subsystem is ~15 commits, each correcting
 * whichever cause was live that week. The message never said which one it was,
 * so every new cause looked like a regression of the last fix.
 *
 * ── the guardrail ──────────────────────────────────────────────────────────
 *
 * The union member IS the reason. Adding a cause without adding its own
 * sentence fails TYPECHECK at {@link describeModelPickerEmptyReason} — a new
 * cause cannot borrow another's words, because it cannot share its member.
 * Review is not load-bearing here; the compiler is.
 */
export type ModelPickerEmptyReason =
  /** No project chosen yet, so there is no runtime to have a catalog at all. */
  | { readonly kind: "no-project-selected" }
  /** A project is chosen, but no runtime has reported what it can execute. */
  | { readonly kind: "workspace-not-started" }
  /** A runtime reported its providers and none of them can execute work. */
  | { readonly kind: "no-executable-provider" }
  /** Models exist; the current search text simply matches none of them. */
  | { readonly kind: "search-miss" };

/**
 * Resolve why the visible list is empty. ALWAYS returns a reason.
 *
 * There is no "not empty" case on purpose: this is consulted only from inside
 * `ComboboxEmpty`, which renders when the visible list is already empty. An
 * earlier draft returned `null` when any models existed at all, which made a
 * search that matched nothing render NOTHING instead of "No models found" —
 * caught by two pre-existing mounted tests. `hasAnyModels` is about the
 * catalog, never about what survived the search filter.
 *
 * Order is the causal chain, outermost first, and is load-bearing: without a
 * project there is no runtime, so "no project" must be reported before
 * "project not started" — otherwise the outer cause is masked by the inner
 * one, which is exactly the 2026-08-07 bug.
 */
export function resolveModelPickerEmptyReason(input: {
  /**
   * Whether the catalog holds any models at all. When it does, the only reason
   * the VISIBLE list can be empty is that the search matched none of them —
   * every deeper cause is already disproven by the catalog being populated.
   */
  readonly hasAnyModels: boolean;
  /**
   * Whether a target project is selected. `undefined` means "this surface has
   * no project concept" (e.g. an existing thread, which is already bound to
   * one) and is deliberately NOT treated as "unselected" — reporting a missing
   * project on a surface that cannot select one would be a new wrong message.
   */
  readonly projectSelected: boolean | undefined;
  /** `undefined` = no runtime has reported yet; `[]` = it reported nothing. */
  readonly providers: ReadonlyArray<ServerProvider> | undefined;
}): ModelPickerEmptyReason {
  if (input.hasAnyModels) return { kind: "search-miss" };
  if (input.projectSelected === false) return { kind: "no-project-selected" };
  if (input.providers === undefined) return { kind: "workspace-not-started" };
  // `[]` is "reported, and nothing was executable" only when it is non-empty;
  // an empty array carries no evidence either way, so it stays a search miss
  // rather than accusing the user of having no provider connected.
  if (input.providers.length > 0 && !input.providers.some(isServerProviderExecutable)) {
    return { kind: "no-executable-provider" };
  }
  return { kind: "search-miss" };
}

/**
 * The user-visible sentence for each reason.
 *
 * Every branch names its own cause AND the action that resolves it. The two are
 * separate obligations: the 2026-08-07 message named a plausible cause but
 * prescribed an action ("Send to start it") that could not work.
 */
export function describeModelPickerEmptyReason(reason: ModelPickerEmptyReason): string {
  switch (reason.kind) {
    case "no-project-selected":
      return "Choose a project to see its models.";
    case "workspace-not-started":
      return "Models load when this project starts. Send to start it.";
    case "no-executable-provider":
      return "Connect a provider in Settings to choose a model.";
    case "search-miss":
      return "No models found";
    default: {
      // Repo idiom (see cloudProjectsGroup.logic.ts, workbarAgents.logic.ts):
      // compile-time exhaustive via the `never` assignment, with a runtime
      // fallback because a bundle can outlive the type that produced it.
      const _exhaustive: never = reason;
      void _exhaustive;
      return "No models found";
    }
  }
}
